import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store/store';
import { regenerateReport } from '../rtk-apis/reports/regenerateReport';
import { clearRegenerateReport } from '../store/slices/reports/regenerateReport';

/**
 * Regenerate flow shared by the express page and the viewer.
 *
 * The server checks the request and returns the clinic's current template. The report
 * panel is then reopened with that raw template and `from: 'regenerate'`, so the editor
 * fills it from the SR exactly as on first generate and autosave writes it over the old
 * body. No copy of the old body is kept anywhere.
 */
type Options = {
  /** Save the open panel's pending typing and close it, so it can't land after the rebuild. */
  onBeforeRegenerate?: () => Promise<void> | void;
  /** Open the report panel with the template object built here. */
  onRegenerated: (reportTemplate: any) => void;
};

export function useRegenerateReport({ onBeforeRegenerate, onRegenerated }: Options) {
  const dispatch = useDispatch<AppDispatch>();
  const [target, setTarget] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestRegenerate = useCallback((report: any) => {
    if (!report?._id) return;
    setError(null);
    setTarget(report);
  }, []);

  const cancel = useCallback(() => {
    if (busy) return;
    setTarget(null);
    setError(null);
  }, [busy]);

  const confirm = useCallback(async () => {
    if (!target?._id) return;
    setBusy(true);
    setError(null);
    try {
      // The server sends the template plus a fresh copy of the fields the rebuild keeps.
      const tpl = await dispatch(regenerateReport({ reportId: target._id })).unwrap();
      const report = tpl.report;

      await onBeforeRegenerate?.();

      onRegenerated({
        item: {
          templateHTML: tpl.templateHTML,
          templateName: tpl.templateName,
          reportType: report.reportType,
          // Read by the editor for status, sonographer and cardiologist.
          data: {
            status: report.status,
            createdByName: report.createdByName,
            reportAssignedTo: report.reportAssignedTo,
            reportAssignedToId: report.reportAssignedToId,
          },
        },
        hospitalHeader: tpl.hospitalHeader,
        hospitalLogo: tpl.hospitalLogo,
        colorCode: Array.isArray(report.colorCode) ? report.colorCode[0] : report.colorCode,
        from: 'regenerate',
        // New identity for the panel so it remounts and starts clean.
        regenerateNonce: Date.now(),
        _id: report._id,
        sharedClipsId: report.sharedClipsId,
        sharedClipsUrl: report.sharedClipsUrl,
        sharedClipsName: report.sharedClipsName,
        attachedClips: report.attachedClips,
        reportAssignedToId: report.reportAssignedToId,
        reportAssignedTo: report.reportAssignedTo,
        status: report.status,
      });
      setTarget(null);
    } catch (err: any) {
      setError(typeof err === 'string' ? err : err?.message || 'Could not regenerate the report.');
    } finally {
      setBusy(false);
      dispatch(clearRegenerateReport());
    }
  }, [dispatch, onBeforeRegenerate, onRegenerated, target]);

  return {
    requestRegenerate,
    dialogProps: {
      open: Boolean(target),
      busy,
      error,
      templateName: target?.templateName || target?.reportType,
      onCancel: cancel,
      onConfirm: confirm,
    },
  };
}
